import React, { useMemo } from "react";
import { IResourceComponentsProps, useTranslate, useGetIdentity } from "@refinedev/core";
import { Create, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, DatePicker, Col, Row } from "antd";
import dayjs from "dayjs";
import { TunerRole, UserRole, SuperadminRole, ServiceRole } from "../../models/user";

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
    const _t = useTranslate();
    const { data: currentUser } = useGetIdentity();
    const currentUserRoleId = (currentUser as any)?.role?.id;
    const isSuperAdmin = currentUserRoleId === SuperadminRole.id || 
                         currentUserRoleId === String(SuperadminRole.id) || 
                         Number(currentUserRoleId) === SuperadminRole.id;

    const { form, formProps, saveButtonProps, queryResult } = useForm(
        {
            onMutationError: (data, variables, context, isAutoSave) => {
                const errors = data?.response?.data?.errors;
                if ('email' in errors) {
                    if (errors['email'] === 'emailAlreadyExists')
                        form.setFields([
                            {
                                name: 'email',
                                errors: [_t('users.errors.emailAlreadyExists')],
                            },
                        ]);
                }
            }
        },
    );

    const handleFinish = async (values: any) => {
        // Values are already in flat format (serviceProviderId, tenantId)
        // Just pass them through to the original onFinish
        if (formProps.onFinish) {
            return formProps.onFinish(values);
        }
    };

    const selectedRoleId = Form.useWatch(['role', 'id'], form);

    const { selectProps: accountSelectProps } = useSelect({
        resource: "accounts",
        optionLabel: "name",
        queryOptions: {
            enabled: selectedRoleId === UserRole.id,
        },
    });

    const { selectProps: roleSelectProps } = useSelect({
        resource: "roles",
        optionLabel: "name",
    });

    // Filter role options to hide Superadmin and Service roles if user is not superadmin
    // and transform role names to use translations
    const filteredRoleOptions = useMemo(() => {
        if (!roleSelectProps.options) return [];
        let options = roleSelectProps.options;
        
        // Filter out Superadmin and Service roles if user is not superadmin
        if (!isSuperAdmin) {
            options = options.filter((option: any) => {
                const roleId = option.value;
                return roleId !== SuperadminRole.id && roleId !== ServiceRole.id;
            });
        }
        
        // Transform role names to use translations
        return options.map((option: any) => {
            const roleName = option.label || option.name;
            const translationKey = `users.fields.roleValues.${roleName}`;
            return {
                ...option,
                label: _t(translationKey, roleName),
            };
        });
    }, [roleSelectProps.options, isSuperAdmin, _t]);

    const { selectProps: serviceProviderSelectProps } = useSelect({
        resource: "service-providers",
        optionLabel: "name",
        queryOptions: {
            enabled: selectedRoleId === TunerRole.id,
        },
    });

    const { selectProps: tenantSelectProps } = useSelect({
        resource: "tenants",
        optionLabel: "name",
        queryOptions: {
            enabled: isSuperAdmin,
        },
    });

    // Force the provider to be email
    form.setFieldsValue(
        {
            'provider': 'email',
            'status': { 'id': 1 }
        });

    const handleFormValuesChange = (changedValues: any, allValues: any) => {
        if ('role' in changedValues && changedValues.role?.id !== undefined) {
            const roleId = changedValues.role.id;
            if (roleId !== TunerRole.id) {
                form.setFieldsValue({
                    serviceProviderId: undefined,
                });
            }
            if (roleId !== UserRole.id) {
                form.setFieldsValue({
                    account: undefined,
                });
            }
        }
    };

    return (
        <Create saveButtonProps={saveButtonProps}>
            <Form {...formProps} layout="vertical" form={form} onValuesChange={handleFormValuesChange} onFinish={handleFinish}>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={_t("users.fields.email")}
                            name={["email"]}
                            rules={[{ required: true, message: _t("forms.errors.required") }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={_t("users.fields.password")}
                            name={["password"]}
                            rules={[
                                { required: true, message: _t("forms.errors.required") },
                                { min: 6, message: _t("fields.errors.minLength", { min: 6 }) },
                            ]}
                        >
                            <Input.Password />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={_t("users.fields.firstName")}
                            name={["firstName"]}
                            rules={[{ required: true, message: _t("forms.errors.required") }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={_t("users.fields.lastName")}
                            name={["lastName"]}
                            rules={[{ required: true, message: _t("forms.errors.required") }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={_t("users.fields.role")}
                            name={["role", "id"]}
                            rules={[{ required: true, message: _t("forms.errors.required") }]}
                        >
                            <Select {...roleSelectProps} options={filteredRoleOptions} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        {selectedRoleId === UserRole.id && (
                            <Form.Item
                                label={_t("users.fields.account")}
                                name={["account", "id"]}
                                rules={[{ required: false }]}
                            >
                                <Select {...accountSelectProps} />
                            </Form.Item>
                        )}
                        {selectedRoleId === TunerRole.id && (
                            <Form.Item
                                label={_t("users.fields.serviceProvider")}
                                name="serviceProviderId"
                                rules={[{ required: false }]}
                            >
                                <Select {...serviceProviderSelectProps} />
                            </Form.Item>
                        )}
                    </Col>
                </Row>
                {isSuperAdmin && (
                    <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                            <Form.Item
                                label={_t("users.fields.tenant")}
                                name="tenantId"
                                rules={[{ required: false }]}
                            >
                                <Select {...tenantSelectProps} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                <Form.Item
                    label={_t("users.fields.status")}
                    name={["status", "id"]}
                    hidden
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Input />
                </Form.Item>


            </Form>
        </Create>
    );
};
